/** @jsxImportSource react */
import { useState } from 'react'
import { Alert, Box, Button, Paper, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { tasksAPI } from '../api/crm'
import { useTenantKey } from '../hooks/useCrmQueries'
import { selectIsTenantAdmin } from '../store/slices/tenantSlice'
import { getErrorMessage } from '../utils/apiErrors'
import Loading from '../components/common/Loading'
import EmptyState from '../components/common/EmptyState'
import TaskItem from '../components/tasks/TaskItem'
import TaskDialog from '../components/tasks/TaskDialog'
import { GROUP_LABELS, groupTasks } from '../components/tasks/taskModel'

const PAGE = 100

/** The member's to-do list: their tasks grouped by when they're due; also what they assigned, and (admins) everyone's. */
const TasksPage = () => {
  const tenantId = useTenantKey()
  const isAdmin = useSelector(selectIsTenantAdmin)
  const [scope, setScope] = useState('MINE')
  const [status, setStatus] = useState('OPEN')
  const [dialog, setDialog] = useState(null)
  const { data, isLoading, error } = useQuery({
    queryKey: [tenantId, 'tasks', 'list', scope, status],
    queryFn: () => tasksAPI.list({ scope, status: status || undefined, size: PAGE }),
  })
  const tasks = data?.items || []
  const groups = groupTasks(tasks)

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>Tasks</Typography>
        <ToggleButtonGroup size="small" exclusive value={status} onChange={(_, v) => v !== null && setStatus(v)} aria-label="Status">
          <ToggleButton value="OPEN">Open</ToggleButton>
          <ToggleButton value="DONE">Done</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog('new')}>New task</Button>
      </Box>
      <Tabs value={scope} onChange={(_, v) => setScope(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }} variant="scrollable" allowScrollButtonsMobile>
        <Tab value="MINE" label="Assigned to me" />
        <Tab value="CREATED" label="Created by me" />
        {isAdmin && <Tab value="ALL" label="Everyone" />}
      </Tabs>

      {isLoading && <Loading />}
      {error && <Alert severity="error">{getErrorMessage(error)}</Alert>}
      {!isLoading && !error && tasks.length === 0 && (
        <EmptyState title={status === 'OPEN' ? 'Nothing to do' : 'No finished tasks yet'}
          description={status === 'OPEN' ? 'Add tasks here or from any record to get reminders.' : 'Completed tasks show up here.'} />
      )}
      {Object.entries(groups).filter(([, list]) => list.length > 0).map(([key, list]) => (
        <Paper key={key} variant="outlined" sx={{ px: 2, py: 1, mb: 2 }}>
          <Typography variant="subtitle2" color={key === 'overdue' ? 'error.main' : 'text.secondary'} sx={{ mb: 0.5 }}>
            {GROUP_LABELS[key]} · {list.length}
          </Typography>
          {list.map((t) => <TaskItem key={t.id} task={t} showAssignee={scope !== 'MINE'} onEdit={setDialog} />)}
        </Paper>
      ))}
      {data && data.totalItems > tasks.length && (
        <Typography variant="body2" color="text.secondary">Showing the first {tasks.length} of {data.totalItems}.</Typography>
      )}
      {dialog && <TaskDialog open onClose={() => setDialog(null)} task={dialog === 'new' ? null : dialog} />}
    </Box>
  )
}

export default TasksPage
